import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';
import GetAppIcon from '@material-ui/icons/GetApp';
import { FormattedMessage } from 'react-intl';

import intlEquipped, { propTypes as i18nPropTypes } from '../../../hocs/intlEquipped';
import SimpleLink from '../../SimpleLink';
import { useConfigValue } from '../../../hooks/useConfigValue';
import { useBffApi } from '../../../hooks/useBffApi';
import { useSnackbar } from '../../../redux/snackbar/hooks';
import { TextButton } from '../../Buttons';
import { CONTENT_TYPE_HEADER, CONTENT_TYPES } from '../../../constants/http';
import SnackbarMessage from '../../Snackbars/components/SnackbarMessage';

const InfoBox = ({ invoice, theme, i18n }) => {
    const { formatMessage } = i18n;
    const { filePath, invoiceNumber } = invoice;
    const { myPage: { supportEmail = {}, invoiceSupportLink = {} } } = useConfigValue();
    const [showSnackbar] = useSnackbar();

    const [fileResponse, makeFileRequest] = useBffApi({
        path: filePath,
        options: {
            method: 'GET',
            headers: {
                [CONTENT_TYPE_HEADER]: CONTENT_TYPES.PDF,
            },
        },
    });

    const renderReceipt = () => (
        <div className={ theme.receiptWrapper }>
            <TextButton
                className={ theme.receipt }
                icon={{
                    component: GetAppIcon,
                    position: 'right',
                }}
                onClick={ () => makeFileRequest() }
            >
                <span className={ theme.downloadText }>
                    { formatMessage('invoices.info.pdf') }
                </span>
            </TextButton>
        </div>
    );

    const renderContact = () => (
        <div className={ theme.contact } data-testid="invoice-support-info">
            { (invoiceSupportLink.href || supportEmail.href) && (
                <div>
                    { formatMessage('invoices.info.contact') }
                    <SimpleLink
                        href={ invoiceSupportLink.href || `mailto:${supportEmail.href}` }
                        theme={ theme }
                    >
                        { formatMessage('invoices.info.linkText') }
                    </SimpleLink>
                    .
                </div>
            )}
        </div>
    );

    useEffect(() => {
        const fileName = `${invoiceNumber}.pdf`;
        const retryOnErrorSnackbar = {
            id: fileName,
            message: (
                <SnackbarMessage
                    message={ <FormattedMessage id="invoices.snackbarError.text" /> }
                />
            ),
            action: (
                <TextButton onClick={ () => makeFileRequest() }>
                    <FormattedMessage id="invoices.snackbarError.retry" />
                </TextButton>
            ),
        };

        if (fileResponse.error) {
            showSnackbar(retryOnErrorSnackbar);
        } else if (fileResponse.data) {
            saveAs(fileResponse.data, fileName);
        }
    }, [fileResponse.data]);

    return (
        <>
            { filePath && renderReceipt() }
            { renderContact() }
        </>
    );
};

InfoBox.propTypes = {
    invoice: PropTypes.shape({
        filePath: PropTypes.string.isRequired,
        invoiceNumber: PropTypes.string.isRequired,
    }).isRequired,
    theme: PropTypes.shape({}),
    i18n: i18nPropTypes,
};

export default intlEquipped(InfoBox);
